import './UppyStyles.css'

import { FileInput as UFileInput, StatusBar } from '@uppy/react'
import React, { useEffect, useRef } from 'react'

import { Loader, View } from '~/components'
import { useUppy } from '~/hooks'
import { trackEvent, useTranslation } from '~/lite'

import { EditIcon, InputLoaderContainer, LoaderContainer, RowFlex } from './styles'
import { IFileInputProps } from './types'

export const FileInput: React.FC<IFileInputProps> = ({
  accept,
  onBeforeFileAdded,
  onInputChange,
  withStatus,
  isLoading,
  isUploaded,
  autoProceed = true,
  uploadFileAction,
  skipTracking,
  icon,
  iconColor,
  iconSize = 16,
  buttonText,
  isInputButton
}) => {
  const t = useTranslation()
  const attachedListeners = useRef(false)
  const { uppy, uppyState } = useUppy({
    restrictions: { allowedFileTypes: [...accept] },
    autoProceed,
    onBeforeFileAdded
  })

  useEffect(() => {
    if (!uppy || attachedListeners.current) {
      return
    }

    attachedListeners.current = true

    uppy.on('upload', () => {
      if (!skipTracking) {
        trackEvent('Press', { action: uploadFileAction })
      }
    })

    uppy.on('complete', result => {
      const uploadResult = result.successful[0]
      onInputChange?.(uploadResult.uploadURL as string | undefined, result.failed.length === 0)
      uppy.cancelAll()
    })
  }, [uppy, onInputChange, skipTracking, uploadFileAction])

  if (!uppy) {
    return null
  }

  const loading = Object.keys(uppyState.currentUploads).length > 0 || !!isLoading

  return (
    <View>
      <RowFlex $icon={!!icon} $loading={loading}>
        {loading &&
          (isInputButton ? (
            <InputLoaderContainer>
              <Loader color="#fff" size={26} inline />
            </InputLoaderContainer>
          ) : (
            <LoaderContainer>
              <Loader color="#fff" size={26} inline />
            </LoaderContainer>
          ))}
        {!!icon && !loading && <EditIcon icon={icon} size={iconSize} color={iconColor ?? '#fff'} />}
        {isUploaded && (
          <LoaderContainer>
            <EditIcon icon="checkmark-circle" size={26} color="#fff" marginY={-6} />
          </LoaderContainer>
        )}
        <UFileInput
          uppy={uppy}
          pretty
          inputName="files[]"
          locale={{ strings: { chooseFiles: icon ? '' : t(loading ? 'Uploading...' : buttonText ?? 'Upload file') } }}
        />
      </RowFlex>
      {withStatus && <StatusBar uppy={uppy} hideAfterFinish={false} showProgressDetails />}
    </View>
  )
}
